type VideoSource = {
	src: string;
	type: string;
};

export class ModalVideo {
	private readonly element: HTMLElement | null;
	private videoContainer: HTMLElement | null = null;
	private video: HTMLVideoElement | null = null;
	private sources: HTMLSourceElement[] = [];
	private trigger: HTMLElement | null = null;
	private openers: NodeListOf<HTMLElement> | null = null;

	private readonly selectorPlayer = '.js-video';
	private readonly classActive = 'video--active';

	constructor(element: HTMLElement | null) {
		if (!element) return;
		this.element = element;
		this.init();
	}

	public init = (): void => {
		this.videoContainer = this.element.querySelector(this.selectorPlayer);
		this.video = this.videoContainer?.querySelector('video') ?? null;

		this.bindEvents();
	};

	private getOpenerSelector = (): string => `.js-modal-open[data-modal="#${this.element.id}"]`;

	private bindEvents = (): void => {
		this.openers = document.querySelectorAll(this.getOpenerSelector());

		this.openers.forEach((opener) => {
			opener.addEventListener('click', this.handleOpenerClick, true);
		});

		this.element.addEventListener('modalOpen', this.handleModalOpen);
		this.element.addEventListener('modalClose', this.handleModalClose);
	};

	private unbindEvents = (): void => {
		this.openers?.forEach((opener) => {
			opener.removeEventListener('click', this.handleOpenerClick, true);
		});

		this.element.removeEventListener('modalOpen', this.handleModalOpen);
		this.element.removeEventListener('modalClose', this.handleModalClose);
	};

	private handleOpenerClick = (event: Event): void => {
		this.trigger = event.currentTarget as HTMLElement;
	};

	private handleModalOpen = (): void => {
		if (!this.video || !this.trigger) return;

		const mp4 = this.trigger.getAttribute('data-video');
		const webm = this.trigger.getAttribute('data-video-webm');
		const poster = this.trigger.getAttribute('data-poster');

		if (!mp4 && !webm) return;

		this.setSources(mp4, webm);

		if (poster) this.setPoster(poster);
	};

	private handleModalClose = (): void => {
		this.stopPlayback();
		this.trigger = null;
	};

	private setSources(mp4: string | null, webm: string | null): void {
		if (!this.video) return;

		const nextSources = this.getNextSources(mp4, webm);

		if (!nextSources.length) return;

		if (this.isSourcesMatch(nextSources)) return;

		this.video.querySelectorAll('source').forEach((source) => source.remove());

		nextSources.forEach(({ src, type }) => {
			const source = document.createElement('source');

			source.src = src;
			source.type = type;
			this.video!.appendChild(source);
		});

		this.videoContainer?.classList.remove(this.classActive);
		this.sources = Array.from(this.video.querySelectorAll('source'));
		this.video.load();
	}

	private getNextSources(mp4: string | null, webm: string | null): VideoSource[] {
		const sources: VideoSource[] = [];

		if (webm) sources.push({ src: webm, type: 'video/webm' });
		if (mp4) sources.push({ src: mp4, type: 'video/mp4' });

		return sources;
	}

	private isSourcesMatch(nextSources: VideoSource[]): boolean {
		if (this.sources.length !== nextSources.length) return false;

		return nextSources.every((next, index) => {
			const current = this.sources[index];

			return current.type === next.type && current.src.endsWith(next.src);
		});
	}

	private setPoster(src: string): void {
		const picture = this.videoContainer?.querySelector<HTMLPictureElement>('.video__play picture');
		if (!picture) return;

		const img = picture.querySelector<HTMLImageElement>('img');

		if (!this.isPosterMatch(img, src)) {
			img.src = src;
			picture.classList.add('loaded');
		}
	}

	private isPosterMatch(img: HTMLImageElement, src: string): boolean {
		return new URL(src, window.location.origin).pathname === new URL(img.src).pathname;
	}

	private stopPlayback(): void {
		this.video?.pause();
		this.videoContainer?.classList.remove(this.classActive);
	}

	public reinit = (): void => {
		this.destroy();
		this.init();
	};

	public destroy = (): void => {
		this.unbindEvents();
		this.stopPlayback();

		this.trigger = null;
		this.videoContainer = null;
		this.video = null;
		this.sources = [];
		this.openers = null;
	};
}
